import { CtaArrow, ctaClasses } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { Container, PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { useDocumentTitle } from '@/lib/seo/use-document-title';

import type { ReactElement } from 'react';

/**
 * Fallback UI rendered by `ErrorBoundary` when a descendant
 * component throws (Phase 7 v1.1 iter 304).
 *
 * Two recovery deep-links — home + trust center — instead of a
 * "try again" button because most marketing-page crashes are bugs,
 * not transient failures (retrying the same tree usually re-throws).
 *
 * The `role="alert"` lives on the inner heading/description block,
 * not on the `<main>` landmark, so the page's main-content
 * landmark stays semantically a `main` rather than being overridden
 * by the `alert` role.
 *
 * **Per-route tab title** (iter 305): calls `useDocumentTitle` so
 * the browser tab reads "Error | Syncanix" instead of leaking the
 * previous route's title (e.g. "Features | Syncanix") while the page
 * itself shows "Something went wrong". Closes the gap with iter-297's
 * per-page title coverage.
 */
export function ErrorFallback(): ReactElement {
  const { t } = useT();
  useDocumentTitle(t('error.documentTitle'));
  return (
    <main id="main" tabIndex={-1} className="min-h-screen bg-[color:var(--bd-page)]">
      {/* `PageHero` rather than a hand-rolled masthead: this page used to carry
          its own copy of the retired `.sx-hero-ambiance` treatment, which is
          exactly how a surface nobody looks at drifts a whole redesign behind
          the rest of the site. `role="alert"` stays on the copy block so the
          failure is announced. */}
      <PageHero
        title={t('error.heading')}
        description={<span role="alert">{t('error.description')}</span>}
      />

      <Container as="section" width="prose" aria-label={t('error.tryLabel')} className="py-14">
        <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:justify-center">
          <Link to="/" className={ctaClasses({ size: 'md' })}>
            <CtaArrow direction="back" />
            {t('error.backHome')}
          </Link>
          <Link to="/trust" className={ctaClasses({ variant: 'secondary', size: 'md' })}>
            {t('error.trustCenter')}
          </Link>
        </div>
      </Container>
    </main>
  );
}
